import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import PageMeta from '../components/PageMeta';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Check, Shield, Star, TrendingUp, ChevronRight, Search, FileCheck } from 'lucide-react';
import { mockData } from '../mock';
import TestimonialSlider from '../components/TestimonialSlider';
import PricingSection from '../components/PricingSection';
import Popup from '../components/Popup';
import MobileCardSlider from '../components/MobileCardSlider';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const ServiceTemplate = () => {
  const { slug } = useParams();
  const service = mockData.services.find(s => s.slug === slug);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const isTrademarkRegistration = slug === 'trademark-registration';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!service) {
    return <div className="min-h-screen flex items-center justify-center">Service not found</div>;
  }

  return (
    <div className="min-h-screen page-transition">
      <PageMeta
        title={service.title}
        description={service.description || `Professional ${service.title} services. Protect your intellectual property with expert legal support from Global Trademark Federation.`}
      />
      {/* Hero Section - Matching Homepage Style */}
      <section
        className="relative bg-cover bg-center pt-28 pb-16 md:pt-32 md:pb-20 overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(45, 48, 71, 0.85), rgba(45, 48, 71, 0.70)), url(${mockData.heroImages.services})`,
        }}
      >
        {/* Animated Background Elements */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDEzNGg4di04aC04djh6bS0xNiAwaDh2LThoLTh2OHptLTE2IDBoOHYtOGgtOHY4em0tMTYgMGg4di04aC04djh6bS0xNiAwaDh2LThoLTh2OHoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-10" />
        
        {/* Subtle Floating Shapes */}
        <div className="absolute top-20 left-10 w-24 h-24 bg-secondary/10 rounded-full blur-xl animate-pulse" />
        <div className="absolute top-40 right-20 w-32 h-32 bg-white/5 rounded-full blur-2xl animate-bounce" style={{ animationDuration: '3s' }} />
        
        <div className="container mx-auto px-4 text-center text-white relative z-10">
          {/* Badge with enhanced animation */}
          <div className="inline-block mb-4 animate-bounce">
            <span className="bg-gradient-to-r from-secondary/30 to-white/15 backdrop-blur-md text-white px-5 py-2 rounded-full text-xs md:text-sm font-bold border-2 border-white/30 shadow-xl flex items-center gap-2">
              <Shield className="w-3 h-3 md:w-4 md:h-4 animate-pulse" />
              Professional Service
            </span>
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold mb-4 leading-tight animate-fade-in">
            {service.title}
          </h1>
          
          <p className="text-lg md:text-xl mb-8 max-w-3xl mx-auto opacity-95 leading-relaxed animate-fade-in" style={{ animationDelay: '0.2s' }}>
            {service.description}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in" style={{ animationDelay: '0.4s' }}>
            <Button 
              onClick={() => setIsPopupOpen(true)}
              size="lg"
              className="bg-secondary hover:bg-secondary/90 text-white font-bold px-12 py-6 text-lg rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 group inline-flex items-center gap-2"
            >
              Get Started Today
              <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </section>

      {/* Informative Section - Trust Indicators */}
      <section className="py-16 text-white relative overflow-hidden" style={{ backgroundImage: `linear-gradient(135deg, rgba(45, 48, 71, 0.95), rgba(45, 48, 71, 0.85))` }}>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0djhoLTh2LThoOHptLTE2IDB2OGgtOHYtOGg4em0xNi0xNnY4aC04di04aDh6bS0xNiAwdjhoLTh2LThoOHptLTE2IDB2OGgtOHYtOGg4eiIvPjwvZz48L2c+PC9zdmc+')] opacity-10" />
        
        <div className="container mx-auto px-4 relative">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Stat 1 */}
            <div className="text-center">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 hover:bg-white/20 transition-all duration-300 hover:scale-105">
                <div className="text-5xl font-bold mb-2 text-secondary">10,000+</div>
                <div className="text-lg font-semibold opacity-95">Trademarks Filed</div>
                <p className="text-sm opacity-80 mt-2">Successfully registered nationwide</p>
              </div>
            </div>

            {/* Stat 2 */}
            <div className="text-center">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 hover:bg-white/20 transition-all duration-300 hover:scale-105">
                <div className="text-5xl font-bold mb-2 text-secondary">98%</div>
                <div className="text-lg font-semibold opacity-95">Success Rate</div>
                <p className="text-sm opacity-80 mt-2">Client satisfaction guaranteed</p>
              </div>
            </div>

            {/* Stat 3 */}
            <div className="text-center">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 hover:bg-white/20 transition-all duration-300 hover:scale-105">
                <div className="text-5xl font-bold mb-2 text-secondary">24/7</div>
                <div className="text-lg font-semibold opacity-95">Expert Support</div>
                <p className="text-sm opacity-80 mt-2">Always here to help you</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Benefits - Key Features */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-gray-900/[0.02] bg-[size:60px_60px]" />
        <div className="absolute top-20 right-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl opacity-50" />
        <div className="absolute bottom-20 left-0 w-96 h-96 bg-accent/10 rounded-full blur-3xl opacity-50" />
        
        <div className="container mx-auto px-4 relative">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="bg-secondary text-white px-6 py-3 rounded-full text-sm font-bold shadow-lg">
                <Star className="w-4 h-4 inline mr-2" />
                Why Choose This Service
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-primary mb-4">Key Benefits</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive protection tailored to your business needs
            </p>
          </div>

          {/* Mobile: Slider, Desktop: Grid */}
          <div className="lg:hidden">
            <MobileCardSlider itemsPerView={1} autoScroll={true} autoScrollInterval={4000}>
              {service.benefits.map((benefit, index) => (
                <div 
                  key={index} 
                  className="min-w-full"
                >
                  <div className="group relative bg-gradient-to-br from-white to-primary/5 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border-2 border-primary/10 hover:border-secondary">
                    <div className="absolute -top-4 -right-4 bg-secondary text-white w-12 h-12 rounded-2xl flex items-center justify-center font-bold text-lg shadow-xl rotate-12 group-hover:rotate-0 transition-transform duration-300">
                      {index + 1}
                    </div>
                    <div className="flex items-start space-x-4">
                      <div className="bg-gradient-to-br from-secondary to-accent w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                        <Check className="w-8 h-8 text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="text-gray-800 font-semibold leading-relaxed text-lg group-hover:text-secondary transition-colors">
                          {benefit}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </MobileCardSlider>
          </div>

          <div className="hidden lg:grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {service.benefits.map((benefit, index) => (
              <div 
                key={index} 
                className="group relative bg-gradient-to-br from-white to-primary/5 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border-2 border-primary/10 hover:border-secondary cursor-pointer"
              >
                <div className="absolute -top-4 -right-4 bg-secondary text-white w-12 h-12 rounded-2xl flex items-center justify-center font-bold text-lg shadow-xl rotate-12 group-hover:rotate-0 transition-transform duration-300">
                  {index + 1}
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-gradient-to-br from-secondary to-accent w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                    <Check className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-800 font-semibold leading-relaxed text-lg group-hover:text-secondary transition-colors">
                      {benefit}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Three-Step Process - Matching Homepage */}
      <section className="py-20 bg-gray-50 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-gray-900/[0.02] bg-[size:50px_50px]" />
        <div className="absolute top-10 left-10 w-72 h-72 bg-accent/10 rounded-full blur-3xl opacity-40" />
        <div className="absolute bottom-10 right-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl opacity-40" />
        
        <div className="container mx-auto px-4 relative">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="bg-secondary text-white px-6 py-3 rounded-full text-sm font-bold shadow-lg">
                How It Works
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-primary mb-4">Three Simple Steps to Protection</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Our streamlined process makes {service.title.toLowerCase()} easy and stress-free
            </p>
          </div>

          {/* Mobile: Slider, Desktop: Grid */}
          <div className="lg:hidden">
            <MobileCardSlider itemsPerView={1} autoScroll={false}>
              {/* Step 1 */}
              <div className="relative group min-w-full">
                <div className="bg-white rounded-3xl p-8 shadow-lg border-2 border-primary/10 min-h-[380px] flex flex-col">
                  <div className="absolute -top-6 -right-6 w-16 h-16 bg-secondary rounded-2xl flex items-center justify-center text-white text-2xl font-bold shadow-xl rotate-12">
                    01
                  </div>
                  <div className="mb-6">
                    <div className="bg-secondary/10 w-20 h-20 rounded-3xl flex items-center justify-center mb-4">
                      <Search className="w-10 h-10 text-secondary" />
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Search & Consultation</h3>
                  <p className="text-gray-600 leading-relaxed flex-1">
                    We conduct a comprehensive search and provide expert consultation to ensure your {service.title.toLowerCase()} is eligible for registration
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="relative group min-w-full">
                <div className="bg-white rounded-3xl p-8 shadow-lg border-2 border-primary/10 min-h-[380px] flex flex-col">
                  <div className="absolute -top-6 -right-6 w-16 h-16 bg-secondary rounded-2xl flex items-center justify-center text-white text-2xl font-bold shadow-xl rotate-12">
                    02
                  </div>
                  <div className="mb-6">
                    <div className="bg-primary/10 w-20 h-20 rounded-3xl flex items-center justify-center mb-4">
                      <FileCheck className="w-10 h-10 text-secondary" />
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Application Filing</h3>
                  <p className="text-gray-600 leading-relaxed flex-1">
                    Our legal team prepares and files your application with the USPTO, handling all the complex paperwork for you
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="relative group min-w-full">
                <div className="bg-white rounded-3xl p-8 shadow-lg border-2 border-primary/10 min-h-[380px] flex flex-col">
                  <div className="absolute -top-6 -right-6 w-16 h-16 bg-secondary rounded-2xl flex items-center justify-center text-white text-2xl font-bold shadow-xl rotate-12">
                    03
                  </div>
                  <div className="mb-6">
                    <div className="bg-secondary/10 w-20 h-20 rounded-3xl flex items-center justify-center mb-4">
                      <Shield className="w-10 h-10 text-secondary" />
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Monitor & Protect</h3>
                  <p className="text-gray-600 leading-relaxed flex-1">
                    We monitor your application status and provide ongoing support until your registration is fully complete and protected
                  </p>
                </div>
              </div>
            </MobileCardSlider>
          </div>

          <div className="hidden lg:grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Step 1 */}
            <div className="relative group h-full">
              <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border-2 border-primary/10 hover:border-secondary h-full flex flex-col min-h-[420px]">
                <div className="absolute -top-6 -right-6 w-16 h-16 bg-secondary rounded-2xl flex items-center justify-center text-white text-2xl font-bold shadow-xl rotate-12 group-hover:rotate-0 transition-transform duration-500">
                  01
                </div>
                <div className="mb-6">
                  <div className="bg-secondary/10 w-20 h-20 rounded-3xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Search className="w-10 h-10 text-secondary" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-secondary transition-colors">Search & Consultation</h3>
                <p className="text-gray-600 leading-relaxed flex-1">
                  We conduct a comprehensive search and provide expert consultation to ensure your {service.title.toLowerCase()} is eligible for registration
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative group h-full">
              <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border-2 border-primary/10 hover:border-secondary h-full flex flex-col min-h-[420px]">
                <div className="absolute -top-6 -right-6 w-16 h-16 bg-secondary rounded-2xl flex items-center justify-center text-white text-2xl font-bold shadow-xl rotate-12 group-hover:rotate-0 transition-transform duration-500">
                  02
                </div>
                <div className="mb-6">
                  <div className="bg-primary/10 w-20 h-20 rounded-3xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <FileCheck className="w-10 h-10 text-secondary" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-secondary transition-colors">Application Filing</h3>
                <p className="text-gray-600 leading-relaxed flex-1">
                  Our legal team prepares and files your application with the USPTO, handling all the complex paperwork for you
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative group h-full">
              <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border-2 border-primary/10 hover:border-secondary h-full flex flex-col min-h-[420px]">
                <div className="absolute -top-6 -right-6 w-16 h-16 bg-secondary rounded-2xl flex items-center justify-center text-white text-2xl font-bold shadow-xl rotate-12 group-hover:rotate-0 transition-transform duration-500">
                  03
                </div>
                <div className="mb-6">
                  <div className="bg-secondary/10 w-20 h-20 rounded-3xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Shield className="w-10 h-10 text-secondary" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-secondary transition-colors">Monitor & Protect</h3>
                <p className="text-gray-600 leading-relaxed flex-1">
                  We monitor your application status and provide ongoing support until your registration is fully complete and protected
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose This Service Section */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-gray-900/[0.02] bg-[size:60px_60px]" />
        
        <div className="container mx-auto px-4 relative">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="bg-secondary text-white px-6 py-3 rounded-full text-sm font-bold shadow-lg">
                <TrendingUp className="w-4 h-4 inline mr-2" />
                Why Choose Us
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-primary mb-4">Expert {service.title}</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Years of experience providing professional legal services with a proven track record of success
            </p>
          </div>
          
          <div className="bg-white rounded-3xl p-10 md:p-12 shadow-2xl border-2 border-primary/10 relative overflow-hidden max-w-5xl mx-auto">
            <div className="absolute top-0 right-0 w-64 h-64 bg-secondary/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-accent/10 rounded-full blur-2xl" />
            
            <div className="space-y-8 text-gray-700 leading-relaxed relative z-10">
              <div className="flex items-start gap-4">
                <div className="bg-secondary/10 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-secondary font-bold">1</span>
                </div>
                <p className="flex-1 text-lg">
                  Our {service.title.toLowerCase()} service is designed to provide <span className="font-semibold text-secondary">comprehensive protection</span> for your business. We handle all aspects of the process, from initial consultation to final registration.
                </p>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="bg-accent/10 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-accent font-bold">2</span>
                </div>
                <p className="flex-1 text-lg">
                  With <span className="font-semibold text-secondary">years of experience</span> and a proven track record, we ensure that your application meets all legal requirements and has the best chance of approval. Our team of experts will guide you through every step.
                </p>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-primary font-bold">3</span>
                </div>
                <p className="flex-1 text-lg">
                  We understand that every business is unique, which is why we offer <span className="font-semibold text-secondary">personalized service</span> tailored to your specific needs. Whether you're a startup or an established company, we have the expertise to protect your intellectual property effectively.
                </p>
              </div>
              
              <div className="mt-10 pt-8 border-t-2 border-gray-100 text-center">
                <Button 
                  onClick={() => setIsPopupOpen(true)}
                  size="lg"
                  className="bg-secondary hover:bg-secondary/90 text-white font-bold px-12 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all hover:scale-105 group inline-flex items-center gap-2"
                >
                  Start Your Application
                  <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section - Only for Trademark Registration */}
      {isTrademarkRegistration && <PricingSection />}

      {/* Testimonials */}
      <TestimonialSlider />

      {/* FAQ Section - Matching Homepage Style */}
      <section className="py-20 bg-gray-50 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-gray-900/[0.02] bg-[size:60px_60px]" />
        <div className="absolute top-1/2 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl opacity-50" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-3xl opacity-50" />
        
        <div className="container mx-auto px-4 relative">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="bg-secondary text-white px-6 py-3 rounded-full text-sm font-bold shadow-lg">
                FAQ
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-primary mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Find answers to common questions about {service.title.toLowerCase()}
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {mockData.faqs.slice(0, 5).map((faq) => (
                <AccordionItem 
                  key={faq.id} 
                  value={`item-${faq.id}`} 
                  className="bg-white rounded-2xl px-8 border-2 border-primary/10 hover:border-secondary hover:shadow-lg transition-all duration-300 group"
                >
                  <AccordionTrigger className="text-left font-bold text-lg text-gray-900 hover:text-secondary py-6 group-hover:translate-x-1 transition-transform">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-700 pb-6 leading-relaxed text-base">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Banner - Matching Homepage Style */}
      <section className="py-24 text-white relative overflow-hidden" style={{ backgroundImage: `linear-gradient(135deg, rgba(45, 48, 71, 0.95), rgba(45, 48, 71, 0.85))` }}>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0djhoLTh2LThoOHptLTE2IDB2OGgtOHYtOGg4em0xNi0xNnY4aC04di04aDh6bS0xNiAwdjhoLTh2LThoOHptLTE2IDB2OGgtOHYtOGg4eiIvPjwvZz48L2c+PC9zdmc+')] opacity-10" />
        
        <div className="absolute top-10 right-10 w-72 h-72 bg-secondary/20 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl" />
        
        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="inline-block mb-6">
              <span className="bg-secondary text-white px-6 py-3 rounded-full text-sm font-bold shadow-xl flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Ready to Get Started?
              </span>
            </div>
            
            <h2 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Protect Your Business Today
            </h2>
            
            <p className="text-xl md:text-2xl mb-10 opacity-95 leading-relaxed max-w-3xl mx-auto">
              Don't wait to secure your brand. Start your {service.title.toLowerCase()} journey with our expert team
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                onClick={() => setIsPopupOpen(true)}
                size="lg"
                className="bg-secondary hover:bg-secondary/90 text-white font-bold px-12 py-6 text-lg rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 group inline-flex items-center gap-2"
              >
                Start Your Application
                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Popup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </div>
  );
};

export default ServiceTemplate;
